import { Listbox, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { bottom, left, right, top } from "@popperjs/core";
import { useEffect, useRef, useState, Fragment } from "react";
import { usePopper } from "react-popper";
import Image from "src/components/Shared/Image";
import { Loader2 } from "src/components/Shared/LoadingAnimations/Loader2";
import { isSVGString } from "src/helpers";
import { classNames } from "src/helpers/classNames";
import useOnClickOutside from "src/hooks/useOnClickOutside";

const MultiSelectObject = ({
  xPlacement = "bottomLeft",
  properElementClassName = "",
  disableProper = false,
  dropdownClassName = "",
  flip = true,
  stratergy = "absolute",
  defaultOptions = [],
  searchable = true,
  searchableFields = [],
  searchComponent = null,
  bubbleComponent = null,
  groupedByField = "",
  placeholder = "Search",
  icon = { image: MagnifyingGlassIcon, name: "MagnifyingGlassIcon" },
  title,
  inline = false,
  disabled = false,
  error = false,
  onChange = () => {},
  uniqueIdentifier = null,
  drowdownWindowMaxWidth = "",
  strictOnChangeMode = true,
  dropdownButtonClass = "",
  isSelectAll = true,
  selectAllText = "Select All",
  emptyListText = "No listed Items",
  showSelectedItems = true,
  showSelectedItemsMaxCount = 5,
  loading = false,
  loadingText = "Loading...",
  loadingSkeletonCount = 3,
  showLoadingInInput = true,
  ...props
}) => {
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [checkedSelectAll, setCheckedSelectAll] = useState(false);
  const [showAllSelectedItems, setShowAllSelectedItems] = useState(false);

  const ref = useRef();
  const popperElRef = useRef(null);
  const [targetElement, setTargetElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const placements = {
    bottom: bottom,
    bottomLeft: "bottom-start",
    bottomRight: "bottom-end",
    top: top,
    topLeft: "top-start",
    topRight: "top-end",
    right: right,
    left: left,
    "": bottom,
  };

  const { styles, attributes } = usePopper(targetElement, disableProper ? null : popperElement, {
    placement: placements[xPlacement] || placements.bottomLeft,
    stratergy,
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 8],
        },
      },
      ...(flip
        ? []
        : [
            {
              name: "flip",
              options: {
                flipVariations: false,
                fallbackPlacements: ["bottom-end"],
              },
            },
          ]),
    ].filter(Boolean),
  });

  useOnClickOutside(ref, () => {
    setPanelOpen(false);
    setKeyword("");
    setFilteredOptions([...defaultOptions]);
  });

  useEffect(() => {
    if (selectedOptions.length === 0 || !strictOnChangeMode) {
      setSelectedOptions([...defaultOptions]);
      setFilteredOptions([...defaultOptions]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOptions, strictOnChangeMode]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!searchable || !searchableFields.length) {
      setFilteredOptions([...selectedOptions]);
      return;
    }

    const getNestedValue = (obj, path) => {
      if (!obj || !path) return "";
      return path.split(".").reduce((acc, part) => acc?.[part], obj);
    };

    const updatedFilteredOptions = selectedOptions.filter((option) => {
      if (!keyword.trim()) return true;

      return searchableFields.some((field) => {
        const value = getNestedValue(option?.object, field);
        return value?.toString().toLowerCase().includes(keyword.toLowerCase());
      });
    });

    setFilteredOptions(updatedFilteredOptions);
  }, [keyword, selectedOptions, searchable, searchableFields, loading]);

  useEffect(() => {
    if (loading) return; // Don't update select all state when loading

    const enabledFilteredOptions = filteredOptions.filter((item) => !item?.disabled);
    const selectedEnabledOptions = enabledFilteredOptions.filter((item) => item?.selected);
    setCheckedSelectAll(enabledFilteredOptions.length > 0 && enabledFilteredOptions.length === selectedEnabledOptions.length);
  }, [filteredOptions, loading]);

  const findObjectValue = (object) => {
    if (!groupedByField || !object) return "";
    const keys = groupedByField.split(".");
    return keys.reduce((value, key) => value?.[key], object) || "";
  };

  const optionClicked = (key, option) => {
    if (!option || loading) return;

    let optionUniqueId = null;

    if (uniqueIdentifier) {
      const keys = uniqueIdentifier.split(".");
      optionUniqueId = keys.reduce((value, key) => {
        return value?.object ? value.object[key] : value?.[key];
      }, option);
    }

    const updatedOptions = selectedOptions.map((opt) => {
      if (opt.key === key) {
        if (uniqueIdentifier) {
          const keys = uniqueIdentifier.split(".");
          const uniqueId = keys.reduce((value, key) => {
            return value?.object ? value.object[key] : value?.[key];
          }, opt);

          if (opt.selected) {
            opt.selected = false;
          } else if (!opt.selected && uniqueId === optionUniqueId) {
            opt.selected = true;
          } else {
            opt.selected = false;
          }
        } else {
          opt.selected = !opt.selected;
        }
      }
      return opt;
    });

    setSelectedOptions(updatedOptions);

    if (!uniqueIdentifier) {
      onChange(updatedOptions);
    } else {
      onChange(updatedOptions, option?.object || {});
    }
  };

  const selectAllOptionClicked = () => {
    if (loading) return;

    const enabledFilteredOptions = filteredOptions.filter((item) => !item?.disabled);
    const shouldSelectAll = !checkedSelectAll;

    const updatedOptions = selectedOptions.map((option) => {
      const isInFilteredEnabled = enabledFilteredOptions.find((item) => item?.key === option?.key);
      return {
        ...option,
        selected: isInFilteredEnabled ? shouldSelectAll : option.selected,
      };
    });

    setSelectedOptions(updatedOptions);
    onChange(updatedOptions);
  };

  useEffect(() => {
    if (ref.current) {
      setTargetElement(ref.current);
    }
  }, []);

  const IconComponent = icon?.image || MagnifyingGlassIcon;

  const shouldShowLoading = loading && panelOpen;
  const shouldShowContent = !loading && filteredOptions.length > 0;
  const shouldShowEmpty = !loading && filteredOptions.length === 0;

  return (
    <div className="relative w-full">
      <Listbox>
        {({ open }) => (
          <>
            {title && !inline && (
              <div className={classNames("mb-1 flex justify-between text-sm font-medium text-gray-700", error ? "text-red-600" : "")}>
                {title}
                <div className="pl-2">{error ? "*Required" : ""}</div>
              </div>
            )}

            <div
              className="relative"
              ref={ref}>
              <div className="relative">
                <div
                  className={classNames(
                    inline ? "relative h-[47px] w-full cursor-pointer rounded-md border border-gray-300 bg-white px-0 pt-2 text-left hover:bg-gray-50 focus:outline-none sm:text-sm md:h-[40px] md:pt-1 2xl:h-[47px] 2xl:pt-2" : "relative flex w-full rounded-md",
                    panelOpen ? "border-highlightColor" : "",
                    disabled ? "cursor-not-allowed" : "",
                    dropdownButtonClass,
                  )}
                  onClick={() => !disabled && setPanelOpen(true)}>
                  {inline && <div className="m-0 flex justify-between pl-3 text-xs font-semibold uppercase leading-none text-gray-400 md:pl-2 md:text-[10px] 2xl:pl-3">{title}</div>}

                  <div className={classNames(inline ? "flex h-8 w-full items-center rounded-none border-none px-3 py-0 leading-none shadow-none focus:ring-0 sm:text-sm md:h-7 md:px-2 2xl:h-8 2xl:px-3" : "w-full")}>
                    <div
                      className={classNames("pointer-events-none absolute left-0 flex items-center pl-3", inline ? "bottom-2" : "inset-y-0")}
                      aria-hidden="true">
                      <IconComponent
                        className="mr-3 h-4 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>

                    <input
                      type="text"
                      disabled={disabled}
                      autoComplete="off"
                      name="search"
                      id="multi-search"
                      value={keyword}
                      className={classNames(
                        disabled ? "cursor-not-allowed bg-gray-50/50 text-gray-400" : "",
                        inline
                          ? "block h-8 w-full rounded-none border-none bg-transparent px-3 py-0 !pl-6 leading-none shadow-none focus:ring-0 sm:text-sm md:h-7 md:px-2 2xl:h-8 2xl:px-3"
                          : "block h-[47px] w-full rounded-md border-gray-300 pl-9 text-sm focus:border-highlightColor focus:ring-0 md:h-[40px] 2xl:h-[47px]",
                      )}
                      placeholder={placeholder}
                      onChange={(e) => setKeyword(e.target.value)}
                    />
                  </div>
                </div>

                <Transition
                  show={panelOpen}
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                  beforeEnter={() => setPopperElement(popperElRef.current)}
                  afterLeave={() => setPopperElement(null)}>
                  <div
                    ref={popperElRef}
                    style={styles.popper}
                    {...attributes.popper}
                    className={classNames("z-50 w-full max-w-full", properElementClassName)}>
                    <div className={classNames("animate-fade-in scrollbar absolute z-50 max-h-56 min-w-96 max-w-full overflow-auto rounded-md bg-white shadow ", drowdownWindowMaxWidth, panelOpen ? "flex flex-col" : "hidden", dropdownClassName)}>
                      {shouldShowLoading && <Loader2 text={loadingText} />}

                      {shouldShowContent && isSelectAll && (
                        <div>
                          <div
                            className={classNames("flex w-full items-center gap-x-3 truncate border-white px-4 py-2", loading ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-gray-100")}
                            onClick={selectAllOptionClicked}>
                            <input
                              type="checkbox"
                              checked={checkedSelectAll}
                              disabled={loading}
                              className={classNames("rounded text-highlightColor focus:ring-highlightColor", loading ? "cursor-not-allowed" : "cursor-pointer")}
                              readOnly
                            />
                            {selectAllText}
                          </div>
                        </div>
                      )}

                      {shouldShowEmpty && <div className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-400 hover:bg-gray-50">{emptyListText}</div>}

                      {shouldShowContent &&
                        filteredOptions.map((option, i, arr) => (
                          <div key={`${option.key}-${option.value || ""}-${option.secondaryValue || ""}`}>
                            {groupedByField && (i === 0 || findObjectValue(arr[i - 1]?.object) !== findObjectValue(option?.object)) && <p className="bg-gray-200/10 p-2 px-3 text-gray-500">{findObjectValue(option?.object)}</p>}

                            <div
                              className={classNames("flex w-full items-center gap-x-3 truncate border-white px-4 py-2", option.disabled || loading ? "cursor-text opacity-50" : "cursor-pointer hover:bg-gray-100")}
                              onClick={() => !option.disabled && !loading && optionClicked(option.key, option)}>
                              <input
                                type="checkbox"
                                checked={option.selected || false}
                                disabled={option.disabled || loading}
                                className={classNames("rounded text-highlightColor focus:ring-highlightColor", option.disabled || loading ? "cursor-text" : "cursor-pointer")}
                                readOnly
                              />
                              {searchComponent ? searchComponent(option.object) : option.value}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </Transition>
              </div>
            </div>

            {showSelectedItems && !loading && selectedOptions.filter((selectedOption) => selectedOption.selected).length > 0 && (
              <div className="scrollbar mt-3 flex flex-wrap gap-2 overflow-auto sm:max-h-[200px]">
                {(() => {
                  const selectedItems = [...new Map(selectedOptions.map((item) => [item["key"], item])).values()].filter((selectedOption) => selectedOption.selected);

                  const itemsToShow = selectedItems.slice(0, showAllSelectedItems ? selectedItems.length : showSelectedItemsMaxCount);

                  return (
                    <>
                      {itemsToShow.map((selectedOption) => (
                        <div
                          className="flex items-center rounded-full border border-highlightColor py-[2px] pl-[8px] pr-[4px] text-highlightColor"
                          key={`${selectedOption.key}-selected`}>
                          {(selectedOption?.image || props?.altIcon) && (
                            <div className="mx-1 flex h-5 w-5 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-white/10">
                              {isSVGString(selectedOption?.image) ? (
                                <div
                                  className="h-5 w-5 text-highlightColor opacity-80"
                                  dangerouslySetInnerHTML={{ __html: selectedOption?.image }}
                                />
                              ) : selectedOption?.image ? (
                                <Image
                                  image={selectedOption?.image}
                                  alt="Icon"
                                />
                              ) : (
                                <div>{props?.altIcon}</div>
                              )}
                            </div>
                          )}

                          {bubbleComponent ? bubbleComponent(selectedOption.object) : <p className="max-h-9 max-w-[240px] truncate pb-[1px] text-center sm:max-w-full">{selectedOption.value}</p>}

                          <div
                            onClick={() => !loading && optionClicked(selectedOption.key, selectedOption)}
                            className={classNames("ml-1 rounded-full p-1", loading ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-gray-100")}>
                            <XMarkIcon className="h-4 w-4 text-highlightColor" />
                          </div>
                        </div>
                      ))}

                      {selectedItems.length > showSelectedItemsMaxCount && (
                        <button
                          onClick={() => !loading && setShowAllSelectedItems(!showAllSelectedItems)}
                          disabled={loading}
                          className={classNames("ml-3 flex h-7 items-center justify-center text-highlightColor", loading ? "cursor-not-allowed opacity-50" : "")}>
                          <span className="underline opacity-70 hover:opacity-100">{showAllSelectedItems ? "Show Less" : "Show more"}</span>
                          <span className="pl-3">{showAllSelectedItems ? "" : `+${selectedItems.length - showSelectedItemsMaxCount} others`}</span>
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {showSelectedItems && loading && selectedOptions.filter((selectedOption) => selectedOption.selected).length > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-highlightColor"></div>
                <span className="text-sm text-gray-500">Updating selections...</span>
              </div>
            )}
          </>
        )}
      </Listbox>
    </div>
  );
};

export default MultiSelectObject;
